import { getUserTimeZone } from 'shared/utils/common';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AuthHeader } from 'services/authHeader';
import { ErrorResponse, ListModel, RejectedValue } from 'shared/interfaces/core-interfaces';
import {
    PayrollSliceModel,
    PayrollMonthlyDataResponse,
    GetPayrollSettingsResponse,
    SavePayrollSettingsPayload,
    PayrollSettingItem,
    GetPayrollsBackendResponse,
    EmployeePayrollBackend,
    AbsencesOvertimeRow,
    AbsenceRow,
    OvertimePaymentRow,
    SaveOvertimePaymentRequest,
    OvertimeHoursDetailsRow,
    OvertimeHoursDetailBackend,
    OvertimeHoursSummaryRow,
    EmployeeOvertimeSummaryBackend,
    GetOvertimePaymentCreateDataResponse,
    OvertimePaymentHistoryRow,
    OvertimePaymentHistoryItemBackend,
    GetOvertimePaymentHistoryResponse,
    PayrollWideColumnDef,
    PayrollEmployeeListItemBackend,
    TABLE_VISIBILITY_SETTING_ID_SET,
    wideColumnAccessorKey,
    CARRIED_MINUS_ACCESSOR_KEY,
    PayrollAbsenceBackend,
} from './payroll-interfaces';
import { API_BASE_URL as baseUri } from 'config/env';

// ─── Transformers ─────────────────────────────────────────────────────────────

function collectAbsencesOvertimeColumns(emps: EmployeePayrollBackend[]): PayrollWideColumnDef[] {
    const seen = new Map<number, PayrollWideColumnDef>();
    for (const emp of emps) {
        for (const cv of emp.columnValues ?? []) {
            if (TABLE_VISIBILITY_SETTING_ID_SET.has(cv.payrollColumnTypeId)) continue;
            if (!seen.has(cv.payrollColumnTypeId)) {
                seen.set(cv.payrollColumnTypeId, {
                    payrollColumnTypeId: cv.payrollColumnTypeId,
                    columnName: cv.columnName,
                });
            }
        }
    }
    return [...seen.values()];
}

function mapEmployeePayrollToRow(emp: EmployeePayrollBackend): AbsencesOvertimeRow {
    const row: AbsencesOvertimeRow = {
        employeeId: emp.employeeId,
        employeeName: emp.employeeName,
        [CARRIED_MINUS_ACCESSOR_KEY]: emp.carriedMinusFromLastMonth ?? 0,
    };
    for (const cv of emp.columnValues ?? []) {
        row[wideColumnAccessorKey(cv.payrollColumnTypeId)] = cv.value;
    }
    return row;
}

function mapToAbsenceRows(absences: PayrollAbsenceBackend[]): AbsenceRow[] {
    return absences.map((a, i) => ({
        key: `${a.employeeId}-${a.from}-${a.to}-${i}`,
        employeeName: a.employeeName,
        from: a.from,
        to: a.to,
        absenceType: a.absenceType,
        status: a.status,
        duration: a.duration,
        files: (a.files ?? []).map(f => ({
            id: f.id,
            fileName: f.fileName,
            createdBy: f.createdBy,
            createdAt: f.createdAt,
            description: f.description ?? null,
        })),
    }));
}

function transformPayrollsResponse(raw: GetPayrollsBackendResponse): PayrollMonthlyDataResponse {
    return {
        settings: raw.settings,
        absencesOvertimeColumns: collectAbsencesOvertimeColumns(raw.employeePayrolls),
        absencesAndOvertimeHours: raw.employeePayrolls.map(mapEmployeePayrollToRow),
        absences: mapToAbsenceRows(raw.absences ?? []),
        refunds: raw.refunds.map(r => ({
            refundId: r.refundId,
            employeeId: r.employeeId,
            employeeName: r.employeeName,
            date: r.date,
            currency: r.currency,
            amount: r.amount,
            tax: r.tax,
            billNumber: r.billNumber,
            description: r.description,
            files: (r.files ?? []).map(f => ({
                id: f.id,
                fileName: f.fileName,
                createdBy: f.createdBy,
                createdAt: f.createdAt,
                description: f.description ?? null,
            })),
        })),
        bonusesAndCommissions: raw.bonusesAndCommissions.map(b => ({
            employeeId: b.employeeId,
            employeeName: b.employeeName,
            type: b.type,
            from: b.from,
            to: b.to,
            amount: b.amount,
            currency: b.currency,
            paymentInterval: b.paymentInterval,
            comment: b.comment ?? '',
        })),
        salaryDeductions: raw.salaryDeductions.map(s => ({
            employeeId: s.employeeId,
            employeeName: s.employeeName,
            type: s.type,
            from: s.from,
            to: s.to,
            amount: s.amount,
            currency: s.currency,
            paymentInterval: s.paymentInterval,
            comment: s.comment ?? '',
        })),
    };
}

function transformOvertimePaymentResponse(raw: GetOvertimePaymentCreateDataResponse): OvertimePaymentRow[] {
    return raw.payments.map(p => ({
        id: p.employeeId,
        employeeId: p.employeeId,
        employeeName: p.employeeName,
        additionalHoursAvailable: p.additionalOvertime,
        additionalHoursToPay: p.monthPaymentAdditionalOvertime,
        regularOvertimeAvailable: p.regularOvertime,
        regularOvertimeToPay: p.monthPaymentRegularOvertime,
        overtimeOverNightAvailable: p.workAtNight,
        overtimeOverNightToPay: p.monthPaymentWorkAtNight,
        overtimeAtWeekendsAvailable: p.workOnDaysOff,
        overtimeAtWeekendsToPay: p.monthPaymentWorkOnDaysOff,
        overtimeOnHolidaysAvailable: p.workOnHoliday,
        overtimeOnHolidaysToPay: p.monthPaymentWorkOnHoliday,
        overtimeOnLeavesAvailable: p.workOnLeave,
        overtimeOnLeavesToPay: p.monthPaymentWorkOnLeave,
        savedOn: p.savedOn,
        description: p.description ?? '',
    }));
}

// Coerce a possibly null/undefined/non-numeric backend value to a finite number,
// defaulting to 0 so NaN never reaches the overtime tables (cells or footer totals).
function toNumber(value: unknown): number {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
}

function mapOvertimeHoursSummary(
    raw: EmployeeOvertimeSummaryBackend[],
    employeeNames: Record<string, string>,
): OvertimeHoursSummaryRow[] {
    return raw.map(d => {
        const totalOt =
            toNumber(d.regularOverTimeHours) +
            toNumber(d.additionalOverTimeHours) +
            toNumber(d.workAtNight) +
            toNumber(d.workOnWeekends) +
            toNumber(d.workOnHoliday) +
            toNumber(d.workOnLeave);
        return {
            employeeId: d.employeeId,
            employeeName: employeeNames[d.employeeId] ?? '',
            targetH: toNumber(d.target),
            currentH: toNumber(d.current),
            additionalHours: toNumber(d.additionalOverTimeHours),
            regularOvertimeHours: toNumber(d.regularOverTimeHours),
            overtimeHoursOverNight: toNumber(d.workAtNight),
            overtimeHoursAtWeekends: toNumber(d.workOnWeekends),
            overtimeHoursOnHolidays: toNumber(d.workOnHoliday),
            overtimeHoursOnLeaves: toNumber(d.workOnLeave),
            minusHours: toNumber(d.unfinishedHours),
            availableOvertimeHours: Math.max(0, totalOt - toNumber(d.unfinishedHours)),
        };
    });
}

function mapOvertimePaymentHistory(raw: OvertimePaymentHistoryItemBackend[]): OvertimePaymentHistoryRow[] {
    return raw.map(p => ({
        id: p.id,
        month: p.month,
        year: p.year,
        additionalHours: p.additionalOvertime,
        regularOvertimeHours: p.regularOvertime,
        overtimeHoursOverNight: p.workAtNight,
        overtimeHoursAtWeekends: p.workOnDaysOff,
        overtimeHoursOnHolidays: p.workOnHoliday,
        overtimeHoursOnLeaves: p.workOnLeave,
        totalOvertimeHours: p.totalOvertime,
        savedOn: p.savedOn,
        description: p.description ?? '',
    }));
}

function mapOvertimeHoursDetails(raw: OvertimeHoursDetailBackend[]): OvertimeHoursDetailsRow[] {
    return raw.map(d => {
        const totalOt =
            toNumber(d.regularOverTimeHours) +
            toNumber(d.additionalOverTimeHours) +
            toNumber(d.workAtNight) +
            toNumber(d.workOnWeekends) +
            toNumber(d.workOnHoliday) +
            toNumber(d.workOnLeave);
        return {
            date: d.date,
            targetH: toNumber(d.target),
            currentH: toNumber(d.current),
            additionalHours: toNumber(d.additionalOverTimeHours),
            regularOvertimeHours: toNumber(d.regularOverTimeHours),
            overtimeHoursOverNight: toNumber(d.workAtNight),
            overtimeHoursAtWeekends: toNumber(d.workOnWeekends),
            overtimeHoursOnHolidays: toNumber(d.workOnHoliday),
            overtimeHoursOnLeaves: toNumber(d.workOnLeave),
            minusHours: toNumber(d.unfinishedHours),
            availableOvertimeHours: Math.max(0, totalOt - toNumber(d.unfinishedHours)),
        };
    });
}

function buildSaveOvertimePaymentBody(request: SaveOvertimePaymentRequest) {
    return {
        payments: request.rows.map(r => ({
            employeeId: r.employeeId,
            monthPaymentRegularOvertime: r.regularOvertimeToPay,
            monthPaymentAdditionalOvertime: r.additionalHoursToPay,
            monthPaymentWorkAtNight: r.overtimeOverNightToPay,
            monthPaymentWorkOnDaysOff: r.overtimeAtWeekendsToPay,
            monthPaymentWorkOnHoliday: r.overtimeOnHolidaysToPay,
            monthPaymentWorkOnLeave: r.overtimeOnLeavesToPay,
            description: r.description || null,
            month: request.month,
            year: request.year,
        })),
    };
}

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const GetPayrollEmployees = createAsyncThunk<ListModel<string>[], void, { rejectValue: RejectedValue }>(
    'Slice/PayrollSlice/GetPayrollEmployees',
    async (_, { rejectWithValue }) => {
        // Tenant is resolved server-side from the auth token; this endpoint returns only the
        // logged-in tenant's active employees, ordered by last name.
        const response = await fetch(baseUri + 'api/employee/getAllActiveByTenant', {
            method: 'GET',
            headers: AuthHeader(),
        });

        if (response.status !== 200) {
            const err = await response.json() as ErrorResponse;
            return rejectWithValue({ message: err.detail, status: err.status });
        }

        const employees = await response.json() as PayrollEmployeeListItemBackend[];
        // Preserve the dropdown label format used previously: "ABBR - LastName FirstName".
        return employees.map(e => ({
            value: e.id ?? null,
            label: `${e.abbreviation} - ${e.lastName} ${e.firstName}`,
        }));
    }
);

export const GetPayrollMonthlyData = createAsyncThunk<PayrollMonthlyDataResponse, { employeeIds: string[]; year: number; month: number }, { rejectValue: RejectedValue }>(
    'Slice/PayrollSlice/GetPayrollMonthlyData',
    async (params, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/payroll/payrolls', {
            method: 'POST',
            headers: AuthHeader(),
            body: JSON.stringify({
                employeeIds: params.employeeIds,
                month: params.month,
                year: params.year,
                timeZone: getUserTimeZone(),
            }),
        });
        if (response.status === 200) {
            const raw = await response.json() as GetPayrollsBackendResponse;
            return transformPayrollsResponse(raw);
        }
        const err = await response.json() as ErrorResponse;
        return rejectWithValue({ message: err.detail, status: err.status });
    }
);

// Downloads a single absence attachment by file id. The GetPayrolls response exposes absence file
// ids (unlike refunds, which omit the parent refund id the refund-download endpoint needs), so we can
// fetch the content on demand. Self-contained here to keep Payroll decoupled from the Absences slice.
export const DownloadPayrollAbsenceFile = createAsyncThunk<{ fileName: string; fileBase64: string }, string, { rejectValue: RejectedValue }>(
    'Slice/PayrollSlice/DownloadPayrollAbsenceFile',
    async (fileId, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/absence/downloadabsencefile?absenceFileId=' + fileId, {
            method: 'GET',
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            return await response.json() as { fileName: string; fileBase64: string };
        }
        const err = await response.json() as ErrorResponse;
        return rejectWithValue({ message: err.detail, status: err.status });
    }
);

// Downloads a single refund attachment. Needs both the parent refund id and the file id — the
// GetPayrolls response now exposes `refundId` per refund row (backend commit c0b8faa21). Self-contained
// here to keep Payroll decoupled from the Refunds slice; mirrors DownloadPayrollAbsenceFile.
export const DownloadPayrollRefundFile = createAsyncThunk<{ fileName: string; fileBase64: string }, { refundId: string; fileId: string }, { rejectValue: RejectedValue }>(
    'Slice/PayrollSlice/DownloadPayrollRefundFile',
    async ({ refundId, fileId }, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/refunds/download-file?id=${refundId}&fileId=${fileId}`, {
            method: 'GET',
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            return await response.json() as { fileName: string; fileBase64: string };
        }
        const err = await response.json() as ErrorResponse;
        return rejectWithValue({ message: err.detail, status: err.status });
    }
);

export const GetPayrollSettings = createAsyncThunk<GetPayrollSettingsResponse, void, { rejectValue: RejectedValue }>(
    'Slice/PayrollSlice/GetPayrollSettings',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/payroll/settings', {
            method: 'GET',
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            return await response.json() as GetPayrollSettingsResponse;
        }
        const err = await response.json() as ErrorResponse;
        return rejectWithValue({ message: err.detail, status: err.status });
    }
);

export const SavePayrollSettings = createAsyncThunk<GetPayrollSettingsResponse, SavePayrollSettingsPayload, { rejectValue: RejectedValue }>(
    'Slice/PayrollSlice/SavePayrollSettings',
    async (payload, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/payroll/settings/save', {
            method: 'PUT',
            headers: AuthHeader(),
            body: JSON.stringify({
                settings: payload.settings,
                carryMinusHoursToNextMonth: payload.carryMinusHoursToNextMonth,
            }),
        });
        if (response.status === 200) {
            return await response.json() as GetPayrollSettingsResponse;
        }
        const err = await response.json() as ErrorResponse;
        return rejectWithValue({ message: err.detail, status: err.status });
    }
);

export const ResetPayrollSettingsToDefault = createAsyncThunk<GetPayrollSettingsResponse, void, { rejectValue: RejectedValue }>(
    'Slice/PayrollSlice/ResetPayrollSettingsToDefault',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/payroll/settings/set-default', {
            method: 'POST',
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            return await response.json() as GetPayrollSettingsResponse;
        }
        const err = await response.json() as ErrorResponse;
        return rejectWithValue({ message: err.detail, status: err.status });
    }
);

export const GetOvertimePayments = createAsyncThunk<OvertimePaymentRow[], { employeeIds: string[]; year: number; month: number }, { rejectValue: RejectedValue }>(
    'Slice/PayrollSlice/GetOvertimePayments',
    async (params, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/payroll/overtime-payment/create-data', {
            method: 'POST',
            headers: AuthHeader(),
            body: JSON.stringify({
                employeeIds: params.employeeIds,
                month: params.month,
                year: params.year,
            }),
        });
        if (response.status === 200) {
            const raw = await response.json() as GetOvertimePaymentCreateDataResponse;
            return transformOvertimePaymentResponse(raw);
        }
        const err = await response.json() as ErrorResponse;
        return rejectWithValue({ message: err.detail, status: err.status });
    }
);

export const SaveOvertimePayment = createAsyncThunk<void, SaveOvertimePaymentRequest, { rejectValue: RejectedValue }>(
    'Slice/PayrollSlice/SaveOvertimePayment',
    async (data, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/payroll/overtime-payment/save', {
            method: 'POST',
            headers: AuthHeader(),
            body: JSON.stringify(buildSaveOvertimePaymentBody(data)),
        });
        if (response.status === 200) return;
        const err = await response.json() as ErrorResponse;
        return rejectWithValue({ message: err.detail, status: err.status });
    }
);

export const GetOvertimeHoursDetails = createAsyncThunk<OvertimeHoursDetailsRow[], { employeeId: string; year: number; month: number }, { rejectValue: RejectedValue }>(
    'Slice/PayrollSlice/GetOvertimeHoursDetails',
    async (params, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/payroll/overtime-hours-details', {
            method: 'POST',
            headers: AuthHeader(),
            body: JSON.stringify({
                employeeId: params.employeeId,
                month: params.month,
                year: params.year,
                timeZone: getUserTimeZone(),
            }),
        });
        if (response.status === 200) {
            const raw = await response.json() as OvertimeHoursDetailBackend[];
            return mapOvertimeHoursDetails(raw);
        }
        const err = await response.json() as ErrorResponse;
        return rejectWithValue({ message: err.detail, status: err.status });
    }
);

export const GetOvertimeHoursSummary = createAsyncThunk<OvertimeHoursSummaryRow[], { employeeIds: string[]; year: number; month: number; employeeNames: Record<string, string> }, { rejectValue: RejectedValue }>(
    'Slice/PayrollSlice/GetOvertimeHoursSummary',
    async (params, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/payroll/overtime-hours-summary', {
            method: 'POST',
            headers: AuthHeader(),
            body: JSON.stringify({
                employeeIds: params.employeeIds,
                month: params.month,
                year: params.year,
                timeZone: getUserTimeZone(),
            }),
        });
        if (response.status === 200) {
            const raw = await response.json() as EmployeeOvertimeSummaryBackend[];
            return mapOvertimeHoursSummary(raw, params.employeeNames);
        }
        const err = await response.json() as ErrorResponse;
        return rejectWithValue({ message: err.detail, status: err.status });
    }
);

export const GetOvertimePaymentHistory = createAsyncThunk<OvertimePaymentHistoryRow[], { employeeId: string }, { rejectValue: RejectedValue }>(
    'Slice/PayrollSlice/GetOvertimePaymentHistory',
    async (params, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/payroll/overtime-payment/list', {
            method: 'POST',
            headers: AuthHeader(),
            body: JSON.stringify({ employeeId: params.employeeId }),
        });
        if (response.status === 200) {
            const raw = await response.json() as GetOvertimePaymentHistoryResponse;
            return mapOvertimePaymentHistory(raw.payments ?? []);
        }
        const err = await response.json() as ErrorResponse;
        return rejectWithValue({ message: err.detail, status: err.status });
    }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const initialState: PayrollSliceModel = {
    statusCode: '',
    message: '',
    showNotification: false,
    isLoading: false,
};

export const PayrollSlice = createSlice({
    name: 'PayrollSlice',
    initialState,
    reducers: {
        resetStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
        },
    },
    extraReducers(builder) {
        const addRejected = (thunk: any) => {
            builder.addCase(thunk.rejected, (state, action: any) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? '500';
            });
        };

        builder
            .addCase(SavePayrollSettings.fulfilled, (state) => {
                state.message = 'Settings saved successfully';
                state.showNotification = true;
                state.statusCode = '200';
            })
            .addCase(ResetPayrollSettingsToDefault.fulfilled, (state) => {
                state.message = 'Settings reset to default';
                state.showNotification = true;
                state.statusCode = '200';
            })
            .addCase(SaveOvertimePayment.fulfilled, (state) => {
                state.message = 'Payment saved successfully';
                state.showNotification = true;
                state.statusCode = '200';
            });

        [
            GetPayrollEmployees,
            GetPayrollMonthlyData,
            DownloadPayrollAbsenceFile,
            DownloadPayrollRefundFile,
            GetPayrollSettings,
            SavePayrollSettings,
            ResetPayrollSettingsToDefault,
            GetOvertimePayments,
            SaveOvertimePayment,
            GetOvertimeHoursDetails,
            GetOvertimeHoursSummary,
            GetOvertimePaymentHistory,
        ].forEach(addRejected);
    },
});

export const { resetStatusCode } = PayrollSlice.actions;
export default PayrollSlice.reducer;
